/**
 * Error Boundary Component
 *
 * Global and sectional error boundaries to catch React errors gracefully.
 * Provides fallback UI and error logging for production debugging.
 *
 * Usage:
 * - Global: Wrap <App> in main.tsx
 * - Sectional: Wrap individual sections with sectionName prop
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { logger } from '../utils/logger';

interface ErrorBoundaryProps {
  children: ReactNode;
  sectionName?: string;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error details
    this.setState({
      error,
      errorInfo,
    });

    // Log to DataDog with traceability context
    logger.error('React component error', {
      errorType: error.name,
      componentStack: errorInfo.componentStack?.substring(0, 500), // Limit stack length
      sectionName: this.props.sectionName || 'global',
    }, error);

    // Also log to console in development for debugging
    if (import.meta.env.DEV) {
      console.error('Error Boundary caught an error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Component Stack:', errorInfo.componentStack);
    }
  }

  handleReset = (): void => {
    if (this.props.onReset) {
      this.props.onReset();
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const { sectionName } = this.props;
      const { error } = this.state;

      // Sectional error boundary - smaller fallback
      if (sectionName) {
        return (
          <div
            className="bg-red-50 border border-red-200 rounded-lg p-6 m-4"
            role="alert"
            aria-live="assertive"
          >
            <div className="flex items-start">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-900 mb-2">
                  Erreur dans la section {sectionName}
                </h3>
                <p className="text-red-700 mb-4">
                  Une erreur s'est produite lors du chargement de cette section.
                  Veuillez réessayer ou continuer votre réclamation.
                </p>
                {import.meta.env.DEV && error && (
                  <details className="mt-4 text-sm text-red-600">
                    <summary className="cursor-pointer font-medium mb-2">
                      Détails techniques (dev)
                    </summary>
                    <pre className="bg-red-100 p-3 rounded overflow-auto text-xs">
                      {error.message}
                      {'\n\n'}
                      {error.stack}
                    </pre>
                  </details>
                )}
                <button
                  onClick={this.handleReset}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Réessayer la section
                </button>
              </div>
            </div>
          </div>
        );
      }

      // Global error boundary - full page fallback
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>

              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Une erreur s'est produite
              </h1>

              <p className="text-lg text-gray-600 mb-8">
                Nous sommes désolés, une erreur inattendue s'est produite.
                Vos données ont été sauvegardées automatiquement.
              </p>

              {import.meta.env.DEV && error && (
                <details className="text-left mb-8 bg-gray-100 p-4 rounded-lg">
                  <summary className="cursor-pointer font-semibold text-gray-800 mb-3">
                    Détails techniques (mode développement)
                  </summary>
                  <div className="space-y-2">
                    <div>
                      <span className="font-medium text-gray-700">Message:</span>
                      <p className="text-red-600 text-sm mt-1">{error.message}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Stack Trace:</span>
                      <pre className="bg-white p-3 rounded text-xs overflow-auto mt-1 border border-gray-200">
                        {error.stack}
                      </pre>
                    </div>
                  </div>
                </details>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={this.handleReset}
                  className="inline-flex items-center justify-center px-6 py-3 bg-tenant-600 text-white font-medium rounded-md hover:bg-tenant-700 focus:outline-none focus:ring-2 focus:ring-tenant-500 focus:ring-offset-2 transition-colors"
                >
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Recharger la page
                </button>

                <button
                  onClick={this.handleGoHome}
                  className="inline-flex items-center justify-center px-6 py-3 bg-gray-600 text-white font-medium rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  <Home className="w-5 h-5 mr-2" />
                  Retour à l'accueil
                </button>
              </div>

              <p className="mt-8 text-sm text-gray-500">
                Si le problème persiste, veuillez contacter le support avec le code:
                <br />
                <code className="inline-block mt-2 px-3 py-1 bg-gray-100 rounded text-gray-800 font-mono">
                  ERR-{Date.now()}
                </code>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Sectional Error Boundary wrapper
 * Convenience component for wrapping sections with a named boundary
 */
export const SectionErrorBoundary: React.FC<{
  children: ReactNode;
  sectionName: string;
}> = ({ children, sectionName }) => {
  return (
    <ErrorBoundary sectionName={sectionName}>
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;
